// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { DeleteButton } from '@shared/ui-kit/components/delete-button';
import { createColumnHelper } from '@tanstack/react-table';
import { type Stock } from '@shared/data-access/interfaces/api/ai/screener';
import { Table } from '@shared/ui-kit/components/grouped-table';
import { WatchlistToggleButton } from '@shared/ui-kit/components/table';
import { type FC, useMemo } from 'react';
import { AssetBrand } from '@shared/ui-kit/components/popups';
import { useTranslation } from 'react-i18next';
import { BuySellButton } from '@advisor-app/features/ui/table/components';

export interface StocksTableProps {
  readonly data: Stock[];
  readonly onDeletePosition: (ticker: string) => void;
}

const columnHelper = createColumnHelper<Stock>();

export const StocksTable: FC<StocksTableProps> = ({ data, onDeletePosition }) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor('ticker', {
        header: t('common.ticker', { ns: 'common' }),
        cell: ({ getValue, row }) => (
          <AssetBrand
            ticker={getValue()}
            assetType="stock"
            logoUrl={row.original.url_logo}
            url={row.original.url_morningstar}
          />
        ),
      }),

      columnHelper.accessor('name', {
        header: t('common.name', { ns: 'common' }),
        cell: ({ getValue }) => <span className="uppercase">{getValue()}</span>,
        minSize: 200,
      }),

      columnHelper.accessor('isin', {
        header: t('common.isin', { ns: 'common' }),
        cell: ({ getValue }) => getValue(),
      }),

      columnHelper.accessor('currency', {
        header: t('common.currency', { ns: 'common' }),
        cell: ({ getValue }) => getValue(),
      }),

      columnHelper.accessor('market_cap', {
        header: t('research.market-cap', { ns: 'research' }),
        meta: { alignment: 'end' },
        cell: ({ getValue, row }) => (
          <Table.Data
            format="currency"
            currency={
              row.original.currency !== 'missing' ? row.original.currency : 'USD'
            }
            value={getValue()}
          />
        ),
      }),

      columnHelper.accessor('pe', {
        header: t('research.pe', { ns: 'research' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => <Table.Data value={getValue()} format="number" />,
      }),

      columnHelper.accessor('pb', {
        header: t('research.pb', { ns: 'research' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => <Table.Data value={getValue()} format="number" />,
      }),

      columnHelper.accessor('dividend_yield', {
        header: t('research.dividend-yield', { ns: 'research' }),
        meta: { alignment: 'end' },
        maxSize: 150,
        cell: ({ getValue }) => (
          <Table.Data value={getValue()} format="percentage" />
        ),
      }),
      columnHelper.display({
        id: 'options',
        enableSorting: false,
        size: 200,
        cell: ({ row }) => (
          <div className="flex w-full flex-row items-center justify-end space-x-4">
            <WatchlistToggleButton
              key={row.original.ticker}
              ticker={row.original.ticker}
              assetType="stock"
            />

            <DeleteButton
              onClick={() => onDeletePosition(row.original.ticker)}
              data-testid={'delete-universe-position-' + row.original.ticker}
            />

            <BuySellButton
              ticker={row.original.ticker}
              operation="buy"
              assetType="stock"
            />
          </div>
        ),
      }),
    ],
    [onDeletePosition, t],
  );

  return (
    <Table.Grouped
      columns={columns}
      data={data}
      enablePagination
      data-testid="universe-table"
    />
  );
};
